import { useQuery } from "react-query";
import {
  BaseLogItem,
  ListApiResponseV3,
  LogItem,
  MessageLogItem,
} from "../../types";
import { fetchLogs } from "../../shared/queries";
import { useState } from "react";
import { getFormattedDate } from "../../shared/utils/formatting";
import { formatTime } from "../../shared/utils/common";
import Pagination from "../Pagination";
import PageTitle from "../PageTitle";
import { useTranslation } from "react-i18next";
import Button from "../Button";
import { useHistory } from "react-router-dom";
import { format, isToday, isYesterday } from "date-fns";
import Icon from "../Icon";

interface MessageLogProp {
  logs: LogItem[];
  setQueryParams?: (params: {
    thread_id: string | null;
    query: string | null;
    source: string | null;
  }) => void;
  setCurrentAuthor?: (currentAuthorKey: string) => void;
}

export default function MessageLogList({
  logs,
  setQueryParams,
  setCurrentAuthor,
}: MessageLogProp) {
  const { t } = useTranslation();
  const history = useHistory();

  const handleGoToChat = (
    thread_id: string,
    resv_key: string,
    source: string | null
  ) => {
    history.push(
      `/messaging?thread_id=${thread_id}&query=${resv_key}&source=${source}`
    );
    if (setQueryParams) {
      setQueryParams({ thread_id, query: resv_key, source: source });
    }
    if (setCurrentAuthor) {
      setCurrentAuthor("");
    }
  };

  // console.log("logs ", data);
  function isMessageLogItem(item: LogItem): item is MessageLogItem {
    return "resv_key" in item && "sent_on" in item && "content" in item;
  }
  function capitalizeFirstLetter(val: string) {
    return String(val).charAt(0).toUpperCase() + String(val).slice(1);
  }

  return (
    <div className="space-y-4">
      <PageTitle title={t("logs.labels.types.messages")} />
      {logs?.filter(isMessageLogItem).map((message) => (
        <div key={message.id} className="text-sm text-gray-700">
          <div className="mb-1 flex items-center gap-1">
            <Button
              className="text-cyan-600"
              onClick={() =>
                handleGoToChat(
                  message.message_thread_id,
                  message.resv_key,
                  message.source
                )
              }
              color="info"
              dataTip={t("messaging.goToChat")}
              label={message.resv_key}
              padding="nopad"
            />{" "}
            <span className=" text-gray-500 text-xs flex items-center gap-1">
              <>
                {isToday(new Date(message.sent_on))
                  ? t("messaging.sentToday")
                  : isYesterday(new Date(message.sent_on))
                  ? t("messaging.sentYesterday")
                  : format(new Date(message.sent_on), "dd/MM/yyyy")}
                <Icon
                  name={`assistant_connected_${message.source.toLowerCase()}`}
                  size="20px"
                />
              </>
            </span>
          </div>
          <div>{message.content}</div>
          <div className="mt-2 text-gray-400">
            {message.approval_status === 0 ? (
              <span className="flex items-center gap-1">
                <Icon name="clock" size="20px" />
                {t("messaging.assistant.messageNeedsApproval")}
              </span>
            ) : message.approval_status === 1 ? (
              <>
                <span className="flex items-center gap-1">
                  <Icon name="check" size="20px" />
                  {t("messaging.sentMessage")}
                  {message.authors.length > 0 &&
                    message.authors.map((author) =>
                      message.type === "sent-bot" &&
                      author.id.includes("USERS") ? (
                        <Button
                          className="flex items-center gap-1"
                          color="info"
                          padding="nopad"
                          key={author.id}
                          icon="pencil"
                          label={author.name}
                          onClick={() =>
                            setCurrentAuthor ? setCurrentAuthor(author.id) : ""
                          }
                        />
                      ) : null
                    )}
                </span>
              </>
            ) : message.approval_status === 2 ? (
              <>
                <span className="flex items-center gap-1">
                  <Icon name="close" size="20px" />
                  {capitalizeFirstLetter(
                    t("messaging.assistant.bouncedMessage")
                  )}
                  {message.authors.length > 0 &&
                    message.authors.map((author) =>
                      message.type === "sent-bot" &&
                      author.id.includes("USERS") ? (
                        <span
                          className="flex items-center gap-1"
                          key={author.id}
                        >
                          <Icon name="pencil" size="12px" />
                          {author.name}
                        </span>
                      ) : null
                    )}
                </span>
              </>
            ) : (
              ""
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
